import type {
  BulkActionOperationType,
  ConfigEntityDetail,
  EnvironmentKey,
} from '@web-config-app/core';
import type { BulkActionReportingStore } from './bulk-action-reporting.store';
import { withBestEffortReporting } from './with-best-effort-reporting';

export interface BulkActionReportingContext {
  operationType: BulkActionOperationType;
  domainEntityType: string;
  sourceEnvironment?: EnvironmentKey;
  targetEnvironment?: EnvironmentKey;
}

export interface BulkActionReportingIntegration {
  /** Creates a session and records every entity as `loading`. */
  start: (entities: ConfigEntityDetail[]) => void;
  /**
   * Records per-entity success/error results and marks the session completed.
   * Entities whose IDs appear in `failedEntityIds` are recorded as `error`
   * with `errorDetail`; everything else is `success`.
   */
  finish: (
    entities: ConfigEntityDetail[],
    failedEntityIds: readonly string[],
    errorDetail: string,
  ) => void;
  /** Marks the session as failed with the given reason. */
  fail: (reason: string) => void;
}

const getEntityName = (entity: ConfigEntityDetail): string =>
  entity.attributes.entityMetadata?.name ?? entity.id;

/**
 * Builds the session-tracking hooks that bulk-action hooks (archive, delete,
 * …) call to wire their operation into the reporting store. All methods are
 * no-ops when `store` is undefined and swallow errors via
 * {@link withBestEffortReporting}, so reporting failures never break the
 * underlying operation.
 */
export const createBulkActionReporting = (
  store: BulkActionReportingStore | undefined,
  {
    operationType,
    domainEntityType,
    sourceEnvironment,
    targetEnvironment,
  }: BulkActionReportingContext,
): BulkActionReportingIntegration => ({
  start: (entities) => {
    if (!store) return;
    withBestEffortReporting(() => {
      store.getState().createSession({
        operationType,
        domainEntityType,
        totalItems: entities.length,
        sourceEnvironment,
        targetEnvironment,
      });

      entities.forEach((entity) => {
        store.getState().recordItemResult({
          sourceEntityId: entity.id,
          entityType: domainEntityType,
          entityName: getEntityName(entity),
          status: 'loading',
        });
      });
    });
  },

  finish: (entities, failedEntityIds, errorDetail) => {
    if (!store) return;
    withBestEffortReporting(() => {
      const failedIdSet = new Set(failedEntityIds);
      entities.forEach((entity) => {
        const isFailed = failedIdSet.has(entity.id);
        store.getState().recordItemResult({
          sourceEntityId: entity.id,
          entityType: domainEntityType,
          entityName: getEntityName(entity),
          status: isFailed ? 'error' : 'success',
          ...(isFailed && { error: { detail: errorDetail } }),
        });
      });
      store.getState().completeSession();
    });
  },

  fail: (reason) => {
    if (!store) return;
    withBestEffortReporting(() => {
      store.getState().failSession(reason);
    });
  },
});
